import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { bloodTestsAPI } from '../services/api'

const getStatusBadgeClass = (status) => {
  const statusMap = {
    optimal: 'badge-success',
    normal: 'badge-success',
    borderline: 'badge-warning',
    slightly_abnormal: 'badge-warning',
    abnormal: 'badge-danger',
    critical: 'badge-danger',
    unknown: 'badge-gray',
  }
  return statusMap[status] || 'badge-gray'
}

export default function ComparisonView() {
  const [searchParams] = useSearchParams()
  const currentId = searchParams.get('current')
  const [selectedTests, setSelectedTests] = useState([])

  // Get all user tests for selection
  const { data: allTests } = useQuery({
    queryKey: ['bloodTests'],
    queryFn: async () => {
      const response = await bloodTestsAPI.getAll()
      return response.data.tests
    },
  })

  // Get comparison data when tests are selected
  const { data: comparison, isLoading } = useQuery({
    queryKey: ['comparison', selectedTests],
    queryFn: async () => {
      if (selectedTests.length < 2) return null
      const ids = selectedTests.join(',')
      const response = await bloodTestsAPI.compareMultiple(ids)
      return response.data
    },
    enabled: selectedTests.length >= 2,
  })

  // Auto-select current test if provided
  useEffect(() => {
    if (currentId && !selectedTests.includes(parseInt(currentId))) {
      setSelectedTests([parseInt(currentId)])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only auto-select on initial currentId; selectedTests is managed by user interaction
  }, [currentId])

  const toggleTestSelection = (testId) => {
    if (selectedTests.includes(testId)) {
      setSelectedTests(selectedTests.filter(id => id !== testId))
    } else {
      if (selectedTests.length < 3) {
        setSelectedTests([...selectedTests, testId])
      }
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        {currentId && (
          <Link to={`/blood-tests/${currentId}`} className="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
            ← Back to Dashboard
          </Link>
        )}
        <h1 className="text-3xl font-bold text-gray-900">Compare Blood Tests</h1>
        <p className="text-gray-600 mt-1">Select 2-3 tests to compare side-by-side</p>
      </div>

      {/* Test Selection */}
      <div className="card mb-8">
        <div className="card-header">
          <h2 className="text-xl font-semibold">Select Tests to Compare</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allTests?.map((test) => (
              <button
                key={test.id}
                onClick={() => toggleTestSelection(test.id)}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  selectedTests.includes(test.id)
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={!selectedTests.includes(test.id) && selectedTests.length >= 3}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {new Date(test.test_date).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">{test.lab_name || 'Blood Test'}</p>
                    {test.health_score && (
                      <p className="text-sm font-medium text-primary-600 mt-1">
                        Score: {Math.round(test.health_score)}/100
                      </p>
                    )}
                  </div>
                  {selectedTests.includes(test.id) && (
                    <svg className="h-6 w-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Comparison Results */}
      {selectedTests.length < 2 ? (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-md">
          <p className="text-sm text-blue-700">
            Please select at least 2 tests to compare
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : comparison ? (
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold">Comparison Results</h2>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">
                      Marker
                    </th>
                    {comparison.tests.map((test) => (
                      <th key={test.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {new Date(test.test_date).toLocaleDateString()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-surface-container divide-y divide-gray-200">
                  {Object.entries(comparison.markers_comparison).map(([markerName, values]) => (
                    <tr key={markerName}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-surface-container">
                        {markerName}
                      </td>
                      {comparison.tests.map((test) => {
                        const markerValue = values.find(v => v.test_id === test.id)
                        return (
                          <td key={test.id} className="px-4 py-3 text-center text-sm">
                            {markerValue ? (
                              <div>
                                <div className="font-semibold">
                                  {markerValue.value !== null && markerValue.value !== undefined
                                    ? `${markerValue.value} ${markerValue.unit || ''}`
                                    : markerValue.value_text || 'N/A'}
                                </div>
                                <span className={`badge ${getStatusBadgeClass(markerValue.status)} mt-1`}>
                                  {markerValue.status}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
